import React, { useState } from "react";
import { Space } from "@/data";
import { AvailableDay } from "@/offering/api";
import { Divider } from "@/offering/divider";
import { EventSpaceOrderParams } from "@/offering/order_params";
import { Spacer } from "@/offering/spacer";
import { useGetEventTypes } from "@/offering_v2/hooks/use_get_event_types";
import { getEventSteps } from "@/offering_v2/utils/get_event_steps";
import { TimeSlot } from "../date-time-picker/checkout_datetime_picker";
import {
  DateTimeValue,
  isHourlyValue,
} from "../date-time-picker/date_time_picker";
import { PickingRange } from "../date_range_picker/date_range_picker";
import { EnterGuestsNumberStep } from "./enter_guests_number_step";
import { EventBookingStepTitle } from "./event_booking_step_title";
import { SelectEventStep } from "./select_event_step";
import { SelectSpaceLayoutStep } from "./select_space_layout_step";
import { SelectWhenToHost } from "./select_when_to_host";
import { UpdateStepButton } from "./update_step_button";
import {
  formatDayRange,
  formatMeetingRoomDateRange,
} from "@/offering/format_date_range";
import { useI18n } from "@/lib/i18n/i18n_provider";

export enum BookingEventStep {
  SelectEvent = 1,
  SelectSpaceLayout = 2,
  EnterNumberOfGuests = 3,
  SelectWhenToHost = 4,
}

export interface EventStep {
  stepNumber: BookingEventStep;
  title: string;
  isActive: boolean;
  isDone: boolean;
}

export type EventBookingContainerValue = Pick<
  EventSpaceOrderParams,
  | "eventTypeID"
  | "eventTypeDescription"
  | "layoutID"
  | "attendees"
  | "dateRange"
>;

interface EventBookingContainerProps {
  availableDays?: AvailableDay[];
  timeslots: TimeSlot[];
  space: Space;
  value: EventBookingContainerValue;
  disableRangeSelection?: boolean;
  onChangeMonth: (startDate: Date, endDate: Date) => void;
  onChangeDateTime: (value?: DateTimeValue) => void;
  onChangeEventType: (id?: string, description?: string) => void;
  onChangeLayout: (id?: string) => void;
  onChangeNumberOfGuests: (value?: number) => void;
  disableUpdateFinalStep?: boolean;
}

export function EventBookingContainer({
  availableDays,
  timeslots,
  space,
  value,
  disableRangeSelection,
  disableUpdateFinalStep,
  onChangeMonth,
  onChangeDateTime,
  onChangeEventType,
  onChangeLayout,
  onChangeNumberOfGuests,
}: EventBookingContainerProps) {
  const { fbt, locale } = useI18n();

  const { data: eventTypeList } = useGetEventTypes(space.id);

  const steps = getEventSteps(value);

  const [activeStep, setActiveStep] = useState(
    steps.find((s) => s.isActive)?.stepNumber
  );

  const eventName = eventTypeList?.find(
    (type) => type.id === value.eventTypeID
  )?.name;
  const displayedEventType = value.eventTypeDescription || eventName;

  const handleUpdateSelectEvent = (step: BookingEventStep) => {
    setActiveStep(step);
  };

  const renderStepContent = (step: EventStep) => {
    switch (step.stepNumber) {
      case BookingEventStep.SelectEvent:
        if (step.stepNumber === activeStep && eventTypeList) {
          return (
            <div className="mt-3">
              <SelectEventStep
                onNext={(eventId: string, otherDescription?: string) => {
                  onChangeEventType(eventId, otherDescription);
                  setActiveStep(BookingEventStep.SelectSpaceLayout);
                }}
                options={eventTypeList}
                value={{
                  eventId: value.eventTypeID,
                  otherDescription: value.eventTypeDescription,
                }}
              />
            </div>
          );
        } else if (step.isDone) {
          return (
            <div>
              <div style={{ fontSize: 14 }} className="my-2">
                Event: {displayedEventType}
              </div>
              <UpdateStepButton
                onClick={() => handleUpdateSelectEvent(step.stepNumber)}
              />
            </div>
          );
        }
        break;

      case BookingEventStep.SelectSpaceLayout:
        if (step.stepNumber === activeStep) {
          return (
            <div className="mt-3">
              <SelectSpaceLayoutStep
                onNext={(value) => {
                  onChangeLayout(value);
                  setActiveStep(BookingEventStep.EnterNumberOfGuests);
                }}
                layouts={space.layouts}
                currency={space.currencyCode}
                value={value.layoutID || space.layouts[0]?.id}
              />
            </div>
          );
        } else if (step.isDone) {
          return (
            <div>
              <div style={{ fontSize: 14 }} className="my-2">
                Layout:{" "}
                {space.layouts.find((l) => l.id === value.layoutID)?.name}
              </div>
              <UpdateStepButton
                onClick={() => handleUpdateSelectEvent(step.stepNumber)}
              />
            </div>
          );
        }
        break;

      case BookingEventStep.EnterNumberOfGuests:
        if (step.stepNumber === activeStep) {
          const capacity = space.layouts.find(
            (l) => l.id === value.layoutID
          )?.capacity;
          if (capacity) {
            return (
              <div className="mt-3">
                <EnterGuestsNumberStep
                  onNext={(value) => {
                    onChangeNumberOfGuests(value);
                    setActiveStep(BookingEventStep.SelectWhenToHost);
                  }}
                  maxOfGuests={capacity}
                  value={value.attendees}
                />
              </div>
            );
          }
        } else if (step.isDone) {
          return (
            <div>
              <div style={{ fontSize: 14 }} className="my-2">
                Number of guests: {value.attendees}
              </div>
              <UpdateStepButton
                onClick={() => handleUpdateSelectEvent(step.stepNumber)}
              />
            </div>
          );
        }
        break;

      case BookingEventStep.SelectWhenToHost:
        if (
          step.stepNumber === activeStep ||
          (disableUpdateFinalStep && step.isDone)
        ) {
          return (
            <SelectWhenToHost
              availableDays={availableDays}
              timeslots={timeslots}
              onChangeMonth={onChangeMonth}
              onChangeDateTime={onChangeDateTime}
              value={value.dateRange}
              space={space}
              pickingRange={
                disableRangeSelection
                  ? PickingRange.OneDate
                  : PickingRange.NonConsecutive
              }
              spaceLayoutId={value.layoutID}
            />
          );
        } else if (step.isDone) {
          return (
            <div>
              <div style={{ fontSize: 14 }} className="my-2">
                When:{" "}
                {isHourlyValue(value.dateRange)
                  ? formatMeetingRoomDateRange(value.dateRange, fbt, locale)
                  : value.dateRange?.startDate && value.dateRange.endDate
                  ? formatDayRange(
                      {
                        start: value.dateRange.startDate,
                        end: value.dateRange.endDate,
                      },
                      fbt,
                      locale
                    )
                  : undefined}
              </div>
              <UpdateStepButton
                onClick={() => handleUpdateSelectEvent(step.stepNumber)}
              />
            </div>
          );
        }
        break;
    }
  };

  return (
    <div>
      {steps.map((step) => (
        <div key={step.stepNumber}>
          <EventBookingStepTitle
            step={step.stepNumber}
            title={step.title}
            isActive={step.stepNumber === activeStep}
            isDone={step.stepNumber === activeStep ? false : step.isDone}
          />

          {renderStepContent(step)}

          <Spacer size={32} />
          <Divider />
          <Spacer size={32} />
        </div>
      ))}
    </div>
  );
}
